import axios from 'axios';
import { ElMessage } from 'element-plus';
import {
    empty,
    getToken,
    isCurrentAccessTokenExpired,
    removeToken,
    touchTokenExpiry
} from '@/utils/auth';
import router from '@/router';
import { STORAGE_KEYS } from '@/constants/app';
import { API_CODE } from '@/constants/http';
import { env } from '@/config/env';
import { t } from '@/utils/i18nT';
import { consumeLoginRedirect, rememberCurrentLoginRedirect } from '@/utils/loginRedirect';
import {
    clearPersistentToken,
    shouldSendAuthCookie
} from '@/utils/persistentAuth';
import {
    broadcastLogout,
    onAuthLogout
} from '@/utils/authSync';
import {
    completeAuthSwitch,
    getRequestLang,
    silentLogin
} from '@/utils/silentAuth';
import { buildInformationnetSignature } from '@/utils/requestSignature';
import { clearOrgUiConfigCache } from '@/utils/orgUiConfigCache';
import { getMachineRoadValidationTranslation } from '@/utils/machineRoadPayload.mjs';
import {
    canRetryAuthenticatedNetworkError,
    canRetryAfterSilentLogin,
    isNetworkError,
    prepareNetworkRetry,
    prepareSilentLoginRetry,
    shouldRetryHttpUnauthorized,
    waitForNetworkRetry
} from '@/utils/authSession.mjs';

const REQUEST_TIMEOUT_MS = 60000;

function goMain() {
    const main = process.env.VUE_APP_MAIN || 'home';
    router.replace({
        path: `/${main}`,
        query: { tkey: Date.now() },
    });
}

/**
 * http请求工具类
 *
 * 请求拦截器 负责将客户端标识token值存储并放置在头部保存给服务端
 *
 * 响应拦截器 负责全局处理业务请求的网络或者业务错误
 * 业务体统一为 { code, msg, data }，与 PHP / Android 一致
 */

// 创建一个axios实例
const service = axios.create({
    baseURL: env.BASE_API,
    // 与 PHP 大报表/慢查询兼容；长请求宜单独用更长 timeout 或分块接口
    timeout: REQUEST_TIMEOUT_MS,
});

const activeListRequestSignatures = new Map();

function isPrinterStatusPolling(config) {
    return config?.printerStatusPolling === true
        && config.url === '/adminapi/printerStatus'
        && String(config.method || 'get').toLowerCase() === 'get';
}

function retryRequestAfterSilentLogin(config, msg) {
    if (!canRetryAfterSilentLogin(config)) {
        goLogin({ broadcast: false });
        return Promise.reject(new Error(msg || '501'));
    }

    return silentLogin().then(
        () => {
            prepareSilentLoginRetry(config, getToken(), getRequestLang());
            return service(config);
        },
        (error) => {
            if (isNetworkError(error)) {
                ElMessage({ type: 'error', message: t('httpNetworkUnavailable') });
            }
            goLogin({ broadcast: false });
            return Promise.reject(error);
        }
    );
}

// 请求拦截
function getRequestPayload(data) {
    if (!data) {
        return {};
    }
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (error) {
            return {};
        }
    }
    if (typeof data === 'object') {
        return data;
    }
    return {};
}

function isListPayload(payload) {
    return payload
        && Object.prototype.hasOwnProperty.call(payload, 'rows')
        && Object.prototype.hasOwnProperty.call(payload, 'page');
}

function getListRequestScope(config) {
    const method = String(config?.method || 'get').toLowerCase();
    const url = config?.url || '';
    return `${method}:${url}`;
}

function markActiveListRequest(config) {
    const payload = getRequestPayload(config?.data);
    if (!isListPayload(payload)) {
        return;
    }
    const scope = getListRequestScope(config);
    const signature = buildInformationnetSignature(scope, payload);
    config.__listRequestScope = scope;
    config.__listRequestSignature = signature;
    activeListRequestSignatures.set(scope, signature);
}

function isStaleListResponse(config) {
    const scope = config?.__listRequestScope;
    if (!scope) {
        return false;
    }
    return activeListRequestSignatures.get(scope) !== config.__listRequestSignature;
}

function createStaleListResponseError(config) {
    const error = new Error('stale list response');
    error.__staleListResponse = true;
    error.config = config;
    return error;
}

service.interceptors.request.use(config => {
    config.headers = config.headers || {};
    const token = getToken();
    if (!empty(token)) {
        config.headers['token'] = token; // 让每个请求携带 token
    }
    const lang = getRequestLang();
    config.headers['lang'] = lang;
    if (shouldSendAuthCookie(config)) {
        config.withCredentials = true;
    }
    markActiveListRequest(config);
    return config
}, err => {
    return Promise.reject(err);
});

function goLogin(options = {}) {
    const last = Number(sessionStorage[STORAGE_KEYS.loginThrottle] ?? 0);
    const now = Date.now();
    if (now - last < 2000) {
        return;
    }
    sessionStorage[STORAGE_KEYS.loginThrottle] = now;
    if (options.broadcast !== false) {
        broadcastLogout();
    }
    removeToken();
    clearOrgUiConfigCache();
    if (!isLoginPage()) {
        rememberCurrentLoginRedirect(router.currentRoute?.value);
        router.replace({
            path: "/"
        });
    }
}

function isLoginPage() {
    return empty(window.location.pathname) || "/" == window.location.pathname;
}

/**
 * 统一处理业务 JSON：code + msg + data（与 PHP ExceptionHandle / Android 拦截器约定一致）
 */
function handleBizPayload(res, config) {
    if (res == null || typeof res.code === 'undefined') {
        return Promise.reject(new Error(t('httpIllegalResponse')));
    }
    // Polling errors are rendered by the badge; session transitions still use the shared flow.
    if (isPrinterStatusPolling(config) && ![
        API_CODE.OK, API_CODE.AUTH_SWITCH, API_CODE.TOKEN_INVALID, API_CODE.PTOKEN_INVALID,
        API_CODE.NOT_ADMIN, API_CODE.REDIRECT, API_CODE.WX
    ].includes(Number(res.code))) {
        const error = new Error(res.msg || res.message || t('httpRequestFailed'));
        error.apiCode = Number(res.code);
        error.apiData = res.data;
        return Promise.reject(error);
    }
    // This dialog localizes its bounded diagnostic errors; retain the normal session/auth handling.
    if (config?.machineLogConfig && ['revision_conflict', 'storage_unavailable', 'forbidden',
        'unsupported_device', 'invalid_categories', 'invalid_integer', 'log_config_failed', 'invalid_method'].includes(res.msg)) {
        const error = new Error(res.msg);
        error.apiCode = Number(res.code);
        return Promise.reject(error);
    }
    switch (Number(res.code)) {
        case API_CODE.OK:
            return Promise.resolve(res.data);
        case API_CODE.AUTH_SWITCH: {
            return completeAuthSwitch(res.data).then((data) => {
                if (isLoginPage()) {
                    const tourl = consumeLoginRedirect();
                    if (empty(tourl)) {
                        goMain();
                    } else {
                        router.replace(tourl);
                    }
                }
                return data;
            });
        }
        case API_CODE.VALIDATION_FAILED: {
            const roadValidation = getMachineRoadValidationTranslation(res.data);
            const message = roadValidation
                ? t(roadValidation.key, roadValidation.params)
                : (res.msg || t('httpValidationFailed'));
            ElMessage({ type: 'error', message });
            return Promise.reject(new Error(message || '422'));
        }
        case API_CODE.TOKEN_INVALID:
            return retryRequestAfterSilentLogin(config, res.msg);
        case API_CODE.PTOKEN_INVALID:
            clearPersistentToken();
            goLogin({ broadcast: false });
            ElMessage({ type: 'error', message: res.msg });
            return Promise.reject(new Error(res.msg || '502'));
        case API_CODE.NOT_ADMIN:
            ElMessage({ type: 'error', message: t('httpNotAdminUser') });
            clearPersistentToken();
            removeToken();
            goLogin();
            return Promise.reject(new Error(res.msg || '503'));
        case API_CODE.BIZ_ERROR:
            ElMessage({ type: 'error', message: res.msg || t('httpServerError') });
            return Promise.reject(new Error(res.msg || '500'));
        case API_CODE.REDIRECT:
            router.replace({ path: '/' });
            return Promise.reject(new Error(res.msg || '504'));
        case API_CODE.WX:
            sessionStorage.removeItem('wxToken');
            ElMessage({ type: 'error', message: res.message || res.msg });
            router.replace({ path: '/login_wx' });
            return Promise.reject(new Error(res.msg || '505'));
        default:
            ElMessage({
                type: 'error',
                message: res.msg || t('httpRequestFailed')
            });
            {
                const error = new Error(res.message || res.msg || 'error');
                error.apiCode = Number(res.code);
                error.apiData = res.data;
                return Promise.reject(error);
            }
    }
}

function touchAuthenticatedSession(config, payload) {
    if (empty(config?.headers?.token) || payload == null || typeof payload.code === 'undefined') {
        return;
    }
    const code = Number(payload.code);
    if ([API_CODE.TOKEN_INVALID, API_CODE.PTOKEN_INVALID, API_CODE.NOT_ADMIN].includes(code)) {
        return;
    }
    touchTokenExpiry();
}

// 响应拦截
service.interceptors.response.use(response => {
    if (isStaleListResponse(response.config)) {
        return Promise.reject(createStaleListResponseError(response.config));
    }
    touchAuthenticatedSession(response.config, response.data);
    return handleBizPayload(response.data, response.config);
}, err => {
    if (err?.__staleListResponse) {
        return Promise.reject(err);
    }
    const failedConfig = err?.response?.config || err?.config;
    if (canRetryAuthenticatedNetworkError(err, failedConfig, getToken())) {
        prepareNetworkRetry(failedConfig);
        return waitForNetworkRetry().then(() => service(failedConfig));
    }
    if (isNetworkError(err)
        && isCurrentAccessTokenExpired()
        && canRetryAfterSilentLogin(failedConfig)) {
        return retryRequestAfterSilentLogin(failedConfig, t('loginSessionExpired'));
    }
    if (shouldRetryHttpUnauthorized(err?.response?.status, failedConfig)) {
        return retryRequestAfterSilentLogin(failedConfig, t('httpUnauthorized'));
    }
    const biz = err && err.response && err.response.data;
    if (biz && typeof biz === 'object' && typeof biz.code !== 'undefined') {
        return handleBizPayload(biz, failedConfig);
    }
    if (isPrinterStatusPolling(failedConfig)) {
        return Promise.reject(err);
    }
    let errMsg = "";
    if (err && err.response) {
        switch (err.response.status) {
            case 400:
                errMsg = t('httpRequestError');
                break;

            case 401:
                errMsg = t('httpUnauthorized');
                break;

            case 403:
                errMsg = t('httpForbidden');
                break;

            case 404:
                errMsg = t('httpAddressError', { url: err.response.config.url });
                break;

            case 408:
                errMsg = t('httpTimeout');
                break;

            case 500:
                errMsg = err.response?.data?.message || t('httpInternalError');
                break;

            case 501:
                errMsg = t('httpNotImplemented');
                break;

            case 502:
                errMsg = t('httpBadGateway');
                break;

            case 503:
                errMsg = t('httpServiceUnavailable');
                break;

            case 504:
                errMsg = t('httpGatewayTimeout');
                break;

            case 505:
                errMsg = t('httpVersionNotSupported');
                break;

            default:
                errMsg = t('httpFailedWithStatus', { status: err.response.status });
        }
    } else if (isNetworkError(err)) {
        errMsg = t('httpNetworkUnavailable');
    } else {
        errMsg = err?.message || String(err || '');
    }
    ElMessage({
        type: 'error',
        message: errMsg || t('httpGenericError')
    });
    return Promise.reject(errMsg);
});

onAuthLogout((event) => {
    if (event?.reason === 'account-changed') {
        window.location.replace('/');
        return;
    }
    goLogin({ broadcast: false });
});

export default service
